import React, { useState } from 'react'
import { useServiceRequests } from './ServicesSection'
import { useCandidate } from './CandidateProvider'
import { Button } from '~/lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import safeParse from '~/lib/safeParse'
import { useFormatTime } from '~/hooks/useFormatTime'

const RevisionDialog = ({
  type,
  preWork = null,
  revisionNumber,
  revisionData,
  children,
}) => {
  const { detailItemId, idRef } = useCandidate()
  const { serviceRequests } = useServiceRequests()
  const undefinedItems = [null, undefined, '', 'null', 'undefined']
  const idToUse = !undefinedItems.includes(detailItemId)
    ? detailItemId
    : idRef.current

  const currentServiceRequest = serviceRequests?.find(
    (serviceRequest) => serviceRequest.id == idToUse,
  )
  const getRevision = () => {
    if (revisionData) return revisionData
    if (revisionNumber === undefined) return currentServiceRequest
    if (type === 'Coach')
      return revisionNumber === 2
        ? currentServiceRequest?.coachRevisions?.[
            currentServiceRequest?.coachRevisions?.length - 1
          ]
        : currentServiceRequest?.coachRevisions?.[revisionNumber]
    if (preWork) return preWork
    return currentServiceRequest?.candidateRevisions?.[revisionNumber]
  }
  const revision = getRevision()

  const [open, setOpen] = useState(false)
  const {
    url,
    notes,
    createdAt,
    message,
    linkedinUrl,
    seniorityLevel,
    mostRecentRole,
    desiredRole,
    coachResumeUrl,
    desiredGoal,
    desiredTimeline,
    targetRoles,
    careerLevel,
    interviewTypes,
    targetJobDescription,
    interviewWith,
    interviewType,
    industry,
    industry2,
    sector,
    companySize,
    exampleJob1,
    exampleJob2,
    location,
    coachNotes,
    candidateDocumentUrl,
    candidateDocumentLabel,
    generalTopic,
    coachStarted,
    skillsCertifications,
    achievements,
    educationHistory,
    workExperience,
    coachName,
  } = revision || {}
  const formatTime = useFormatTime()
  const displayCreatedAt = formatTime(createdAt)
  const downloadUrl = preWork ? preWork.url : url || coachResumeUrl
  const formattedNotes = notes ? safeParse(notes.replace(/\n/g, '<br />')) : ''
  const formattedMessage = message
    ? safeParse(message.replace(/\n/g, '<br />'))
    : ''
  const dialogDescrption = coachStarted
    ? `Request opened by Coach ${coachName}`
    : 'Request submitted by Candidate'

  const messageType = coachStarted ? '' : type + ' '

  const submittedByName = type === 'Coach' ? `Coach ${coachName}` : type

  const hidden = coachNotes?.includes('(hidden)')

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="justify-start font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">{type} Message</h2>
          </DialogTitle>
        </DialogHeader>
        {revisionNumber === undefined ? (
          <DialogDescription>
            {dialogDescrption} on {displayCreatedAt}.
          </DialogDescription>
        ) : (
          <DialogDescription>
            {type === 'Coach' ? 'Feedback' : 'Revision'} submitted by{' '}
            {submittedByName} on {displayCreatedAt}.
          </DialogDescription>
        )}
        <div className="max-h-[70vh] overflow-y-auto px-4 pb-4">
          {coachNotes && (
            <div className="mb-4">
              <p className="text-lg font-medium">Internal Coach Note:</p>
              <p className="mt-2">
                {hidden ? 'This note is hidden.' : coachNotes}
              </p>
            </div>
          )}
          {candidateDocumentUrl && (
            <div>
              <a
                href={candidateDocumentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-intoo-blue-medium hover:underline"
              >
                Download {candidateDocumentLabel || 'Candidate Document'}
              </a>
            </div>
          )}
          {downloadUrl && (
            <div>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-intoo-blue-medium hover:underline"
              >
                Download {type} Document
              </a>
            </div>
          )}
          {notes && (
            <div className="mt-4">
              <p className="text-lg font-medium">
                {type} Message Shared with{' '}
                {type === 'Coach' ? 'Candidate' : 'Coach'}:
              </p>
              <p className="mt-2">{formattedNotes}</p>
            </div>
          )}
          {linkedinUrl && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} LinkedIn URL:</p>
              <p className="mt-2">
                <a
                  href={
                    linkedinUrl.startsWith('http://') ||
                    linkedinUrl.startsWith('https://')
                      ? linkedinUrl
                      : `https://${linkedinUrl}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkedinUrl}
                </a>
              </p>
            </div>
          )}
          {desiredGoal && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Desired Goal:</p>
              <p className="mt-2">{desiredGoal}</p>
            </div>
          )}
          {desiredTimeline && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Desired Timeline:</p>
              <p className="mt-2">{desiredTimeline}</p>
            </div>
          )}
          {targetRoles && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Target Roles:</p>
              <p className="mt-2">{targetRoles}</p>
            </div>
          )}
          {careerLevel && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Career Level:</p>
              <p className="mt-2">{careerLevel}</p>
            </div>
          )}
          {interviewTypes && interviewTypes.length > 0 && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Interview Types:</p>
              <ul className="mt-2 list-disc pl-5">
                {interviewTypes.map((interviewType, index) => (
                  <li key={index}>{interviewType}</li>
                ))}
              </ul>
            </div>
          )}
          {targetJobDescription && (
            <div className="mt-4">
              <p className="text-lg font-medium">
                {type} Target Job Description:
              </p>
              <p className="mt-2">
                {targetJobDescription.split(/\r?\n/).map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            </div>
          )}
          {interviewWith && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Interview With:</p>
              <p className="mt-2">{interviewWith}</p>
            </div>
          )}
          {interviewType && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Interview Type:</p>
              <p className="mt-2">{interviewType}</p>
            </div>
          )}
          {industry && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Industry:</p>
              <p className="mt-2">{industry}</p>
            </div>
          )}
          {industry2 && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Industry 2:</p>
              <p className="mt-2">{industry2}</p>
            </div>
          )}
          {sector && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Sector:</p>
              <p className="mt-2">{sector}</p>
            </div>
          )}
          {exampleJob1 && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Example Job 1:</p>
              <p className="mt-2">{exampleJob1}</p>
            </div>
          )}
          {exampleJob2 && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Example Job 2:</p>
              <p className="mt-2">{exampleJob2}</p>
            </div>
          )}
          {companySize && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Company Size:</p>
              <p className="mt-2">{companySize}</p>
            </div>
          )}
          {location && (
            <div className="mt-4">
              <p className="text-lg font-medium">{type} Location:</p>
              <p className="mt-2">{location}</p>
            </div>
          )}
          {generalTopic && (
            <div className="mt-4">
              <p className="text-lg font-medium">General Check-In Topic:</p>
              <p className="mt-2">{generalTopic}</p>
            </div>
          )}
          {message && (
            <div className="mt-4">
              <p className="text-lg font-medium">{messageType}Message:</p>
              <p className="mt-2">{formattedMessage}</p>
            </div>
          )}
          {seniorityLevel && (
            <div className="mt-4">
              <p className="text-lg font-medium">Seniority Level:</p>
              <p className="mt-2">{seniorityLevel}</p>
            </div>
          )}
          {mostRecentRole && (
            <div className="mt-4">
              <p className="text-lg font-medium">Most Recent Role:</p>
              <p className="mt-2">{mostRecentRole}</p>
            </div>
          )}
          {desiredRole && (
            <div className="mt-4">
              <p className="text-lg font-medium">Desired Role:</p>
              <p className="mt-2">{desiredRole}</p>
            </div>
          )}
          {skillsCertifications && (
            <div className="mt-4">
              <p className="text-lg font-medium">Skills & Certifications:</p>
              <p className="mt-2">{skillsCertifications}</p>
            </div>
          )}
          {achievements && (
            <div className="mt-4">
              <p className="text-lg font-medium">Achievements:</p>
              <p className="mt-2">{achievements}</p>
            </div>
          )}
          {educationHistory && (
            <div className="mt-4">
              <p className="text-lg font-medium">Education History:</p>
              <p className="mt-2">{educationHistory}</p>
            </div>
          )}
          {workExperience && (
            <div className="mt-4">
              <p className="text-lg font-medium">Work Experience:</p>
              <p className="mt-2">{workExperience}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RevisionDialog
